import { useCallback } from "react";
import { useGetList } from "ra-core";
import { useWatch } from "react-hook-form";

import type { Contact, LeadSource } from "../types";
import {
  validateContactMandatory,
  type ContactMandatoryField,
} from "./contactModel";

const MAX_LEAD_SOURCES = 200;

/** Flags a validator as required, so the input it validates shows its required marker. */
export const markRequired = <Args extends unknown[], Result>(
  validator: (...args: Args) => Result,
) => Object.assign(validator, { isRequired: true as const });

/**
 * Almikia Epic EC2a: the lead sources catalogue, fetched once (under 1K rows) and filtered
 * locally; shared by the origin dropdown and the mandatory-field validators.
 */
export const useLeadSources = () =>
  useGetList<LeadSource>("lead_sources", {
    pagination: { page: 1, perPage: MAX_LEAD_SOURCES },
    sort: { field: "name", order: "ASC" },
  });

/**
 * Per-field validators built on `validateContactMandatory` (TASK-119), so the form shows the
 * same Spanish messages as the database guard. `validate(field)` is meant for an input's
 * `validate` prop: it reads the whole form values, so cross-field rules (a colega needs a kind,
 * a BPO unit needs a project) fail on the right input. The DB guard stays the authority.
 */
export const useContactMandatory = () => {
  const leadSourceId = useWatch({ name: "lead_source_id" });
  const { data: sources } = useLeadSources();
  const originLinkKind =
    sources?.find((source) => String(source.id) === String(leadSourceId))
      ?.link_kind ?? null;

  const validate = useCallback(
    (field: ContactMandatoryField) =>
      markRequired(
        (_value: unknown, values: Partial<Contact>) =>
          validateContactMandatory(values, { originLinkKind })[field],
      ),
    [originLinkKind],
  );

  return { validate, originLinkKind, sourcesLoaded: sources !== undefined };
};
